import { useMemo } from 'react'

import { type ISelectProps, Select } from '@procurify/ui'

import {
  type CustomField,
  type CustomFieldTypeEnum,
} from '@webapp/core/entities/internal/CustomField'
import { useQueryAllOrderItemCustomFields } from '@webapp/core/features/get-custom-fields/models/useQueryAllOrderItemCustomFields'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

export interface IBudgetCustomFieldSelectProps extends Omit<
  ISelectProps<CustomField>,
  'options' | 'isMulti' | 'value' | 'onChange'
> {
  value: Nullable<CustomField>
  onChange: (customField: Nullable<CustomField>) => void
  /** Offer only these field types. Every type is offered when omitted. */
  fieldTypes?: readonly CustomFieldTypeEnum[]
  /** Fields chosen elsewhere (e.g. another condition row) that can't be picked again. */
  excludedFieldIds?: number[]
}

export const BudgetCustomFieldSelect = ({
  value,
  onChange,
  fieldTypes,
  excludedFieldIds,
  ...props
}: IBudgetCustomFieldSelectProps) => {
  const intl = useProcurifyIntl()
  const { customFields, isLoading } = useQueryAllOrderItemCustomFields()

  const options = useMemo(
    () =>
      customFields.filter(
        (customField) =>
          (!fieldTypes || fieldTypes.includes(customField.type)) &&
          !excludedFieldIds?.includes(customField.id)
      ),
    [customFields, fieldTypes, excludedFieldIds]
  )

  const getOptionLabel = (customField: CustomField) => {
    if (customField.active) return customField.name

    return intl.formatMessage(messages.inactiveCustomFieldLabel, {
      name: customField.name,
    })
  }

  return (
    <Select<CustomField>
      options={options}
      value={value}
      onChange={(customField) => onChange(customField ?? null)}
      isLoading={isLoading}
      isClearable
      placeholder={intl.formatMessage(messages.placeholder)}
      getOptionLabel={getOptionLabel}
      getOptionValue={(customField) => customField.id.toString()}
      {...props}
    />
  )
}
